import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDismissable } from './useDismissable';

export interface AutocompleteFieldProps<T> {
  id: string;
  /** Currently committed selection; drives the input text when the field is idle. */
  value: T | null;
  onSelect: (option: T | null) => void;
  options: T[];
  getOptionKey: (option: T) => string;
  getOptionLabel: (option: T) => string;
  /** When set, options are listed under headers (e.g. country), keeping first-seen group order. */
  getOptionGroup?: (option: T) => string;
  /** `client` filters `options` by the typed term; `none` expects pre-filtered options. */
  filterMode?: 'client' | 'none';
  /** Notified on every keystroke — use it to drive remote option fetching. */
  onInputChange?: (term: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  errorId?: string;
  placeholder?: string;
  loadingText?: string;
  emptyText?: string;
  inputRef?: (node: HTMLInputElement | null) => void;
}

function normalize(term: string): string {
  return term
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

interface OptionGroup<T> {
  label: string;
  options: T[];
}

function groupOptionsByFirstSeen<T>(
  options: T[],
  getOptionGroup: (option: T) => string,
): OptionGroup<T>[] {
  const groups: OptionGroup<T>[] = [];
  const indexByLabel: Record<string, number> = {};

  options.forEach((option) => {
    const label = getOptionGroup(option);
    if (indexByLabel[label] == null) {
      indexByLabel[label] = groups.length;
      groups.push({ label, options: [] });
    }
    groups[indexByLabel[label]].options.push(option);
  });

  return groups;
}

/**
 * Typed combobox used by every searcher form. Works both with a static option
 * list filtered in the browser (packages origins/destinations) and with options
 * fed by a remote typeahead (`filterMode='none'`).
 */
export function AutocompleteField<T>({
  disabled = false,
  emptyText = 'No hay resultados',
  errorId,
  filterMode = 'client',
  getOptionGroup,
  getOptionKey,
  getOptionLabel,
  hasError = false,
  id,
  inputRef,
  isLoading = false,
  loadingText = 'Cargando…',
  onInputChange,
  onSelect,
  options,
  placeholder,
  value,
}: AutocompleteFieldProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [term, setTerm] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedLabel = value ? getOptionLabel(value) : '';
  const inputValue = term ?? selectedLabel;

  const filteredOptions = useMemo(() => {
    if (filterMode === 'none' || term === null || normalize(term) === '') {
      return options;
    }
    const needle = normalize(term);
    return options.filter((option) => normalize(getOptionLabel(option)).includes(needle));
  }, [filterMode, getOptionLabel, options, term]);

  const optionGroups = useMemo(
    () => (getOptionGroup ? groupOptionsByFirstSeen(filteredOptions, getOptionGroup) : null),
    [filteredOptions, getOptionGroup],
  );

  const visibleOptions = useMemo(
    () => (optionGroups ? optionGroups.flatMap((group) => group.options) : filteredOptions),
    [filteredOptions, optionGroups],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
    setTerm(null);
  }, []);

  useDismissable(isOpen, containerRef, close);

  const commit = useCallback(
    (option: T) => {
      onSelect(option);
      close();
    },
    [close, onSelect],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextTerm = event.target.value;
    setTerm(nextTerm);
    setActiveIndex(-1);
    setIsOpen(true);
    onInputChange?.(nextTerm);
    if (nextTerm.trim() === '' && value) {
      onSelect(null);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && !isOpen) {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex(0);
      return;
    }

    if (!isOpen || visibleOptions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((prev) => (prev < visibleOptions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : visibleOptions.length - 1));
        break;
      case 'Enter':
        if (activeIndex >= 0) {
          event.preventDefault();
          commit(visibleOptions[activeIndex]);
        }
        break;
      default:
        break;
    }
  };

  const showEmpty = !isLoading && visibleOptions.length === 0;

  const renderOption = (option: T, index: number) => {
    const optionKey = getOptionKey(option);
    const isSelected = Boolean(value) && getOptionKey(value) === optionKey;

    return (
      <li key={optionKey} role='presentation'>
        <button
          aria-selected={isSelected}
          className={[
            'searcher-option',
            index === activeIndex && 'searcher-option--active',
            isSelected && 'searcher-option--selected',
          ]
            .filter(Boolean)
            .join(' ')}
          id={`${id}-option-${index}`}
          onMouseDown={(event) => {
            event.preventDefault();
            commit(option);
          }}
          role='option'
          type='button'
        >
          {getOptionLabel(option)}
        </button>
      </li>
    );
  };

  const renderGroupedOptions = () => {
    if (!optionGroups) return null;

    let index = 0;

    return optionGroups.map((group) => {
      const groupOptions = group.options.map((option) => {
        const node = renderOption(option, index);
        index += 1;
        return node;
      });

      return (
        <li className='searcher-popover__group' key={group.label} role='presentation'>
          <div className='searcher-popover__group-label'>{group.label}</div>
          <ul className='searcher-popover__group-list'>{groupOptions}</ul>
        </li>
      );
    });
  };

  return (
    <div className='searcher-field__anchor' ref={containerRef}>
      <input
        aria-activedescendant={
          isOpen && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined
        }
        aria-autocomplete='list'
        aria-controls={isOpen ? `${id}-listbox` : undefined}
        aria-describedby={hasError && errorId ? errorId : undefined}
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        aria-invalid={hasError}
        autoComplete='off'
        className={[
          'searcher-field__control',
          'searcher-field__control--input',
          hasError && 'searcher-field__control--invalid',
        ]
          .filter(Boolean)
          .join(' ')}
        disabled={disabled}
        id={id}
        onChange={handleChange}
        onFocus={() => !disabled && setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        ref={inputRef}
        role='combobox'
        type='text'
        value={inputValue}
      />

      {isOpen && (
        <ul className='searcher-popover' id={`${id}-listbox`} role='listbox'>
          {isLoading && (
            <li aria-live='polite' className='searcher-popover__status' role='status'>
              {loadingText}
            </li>
          )}

          {showEmpty && <li className='searcher-popover__status'>{emptyText}</li>}

          {optionGroups
            ? renderGroupedOptions()
            : visibleOptions.map((option, index) => renderOption(option, index))}
        </ul>
      )}
    </div>
  );
}
